import dayjs from "dayjs";
import {AbstractControl, ValidatorFn} from "@angular/forms";

export class DateUtils {
  /**
   * Formata uma data para o formato dd/MM/yyyy.
   * @param date Data no formato Date ou string
   * @returns String formatada (ex: 09/05/2025) ou string vazia se inválida
   */
  static formatDate(date: Date | string | null | undefined): string {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';

    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();

    return `${day}/${month}/${year}`;
  }

  /**
   * Formata data e hora para o formato dd/MM/yyyy HH:mm.
   * @param date Data no formato Date ou string
   * @returns String formatada (ex: 09/05/2025 14:30) ou string vazia se inválida
   */
  static formatDateTime(date: Date | string | null | undefined): string {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';

    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  /**
   * Converte um objeto Date para string no formato 'YYYY-MM-DD' (ex: '2025-05-14'),
   * ideal para campos do tipo LocalDate no backend.
   *
   * @param date Objeto Date a ser convertido
   * @returns String no formato 'YYYY-MM-DD' ou null se valor inválido
   */
  public static toLocalDate(date: Date | undefined): string | null {
    return date ? dayjs(date).format('YYYY-MM-DD') : null;
  }

  /**
   * Converte um objeto Date para string no formato 'HH:mm' (ex: '18:30'),
   * ideal para campos do tipo LocalTime no backend.
   *
   * @param date Objeto Date a ser convertido
   * @returns String no formato 'HH:mm' ou null se valor inválido
   */
  public static toLocalTime(date: Date | null | undefined): string | null {
    return date ? dayjs(date).format('HH:mm') : null;
  }

  /**
   * Converte uma string de horário ("HH:mm:ss" ou "HH:mm") em um objeto Date com hora ajustada.
   * @param timeStr string no formato "HH:mm:ss" ou "HH:mm"
   * @returns Objeto Date com a hora ajustada, ou null se inválido
   */
  static timeStringToDate(timeStr: string | undefined): Date | null {
    if (!timeStr) return null;

    const parts = timeStr.split(':').map(Number);

    if (parts.length < 2 || parts.some(isNaN)) return null;

    const [hours, minutes, seconds] = [parts[0], parts[1], parts[2] || 0];

    const date = new Date();
    date.setHours(hours, minutes, seconds, 0);

    return date;
  }

  /**
   * Retorna uma data no formato ISO `yyyy-MM-dd`, sem hora.
   * Útil para inputs tipo `<input type="date">`.
   * @param date Data no formato Date ou string
   * @returns String no formato ISO (ex: 2025-05-09) ou null se inválida
   */
  static toIsoDateOnly(date: Date | string | undefined): string | undefined {
    if (!date) return undefined;
    const d = new Date(date);
    if (isNaN(d.getTime())) return undefined;
    return d.toISOString().split('T')[0];
  }

  /**
   * Retorna uma data no formato ISO `yyyy-MM-dd`, sem hora.
   * Útil para inputs tipo `<input type="date">`.
   * @param date String formatada (ex: 09/05/2025)
   * @returns String no formato ISO (ex: 2025-05-09) ou null se inválida
   */
  static fromLocaleToIsoDate(date: string) {
    if (!date) return '';
    const parts = date.split('/');
    const value = new Date(`${parts[2]}-${parts[1]}-${parts[0]}T00:00:00`);
    return value.toISOString().split('T')[0];
  }

  /**
   * Converte uma string no formato 'YYYY-MM-DD' em um objeto Date.
   * Retorna null se a string for inválida ou nula.
   */
  static parseIsoDate(dateStr: string | null | undefined): Date | null {
    if (!dateStr) return null;
    const parsed = dayjs(dateStr, 'YYYY-MM-DD');
    return parsed.isValid() ? parsed.toDate() : null;
  }

  static timeRangeValidator(initial: string, final: string): ValidatorFn {
    return (form: AbstractControl): { [key: string]: any } | null => {
      const horaInicial = form.get(initial)?.value;
      const horaFinal = form.get(final)?.value;
      if (!horaInicial || !horaFinal) return null;
      const dataRef = new Date();
      const inicio = new Date(dataRef);
      const fim = new Date(dataRef);

      inicio.setHours(horaInicial.getHours(), horaInicial.getMinutes(), 0);
      fim.setHours(horaFinal.getHours(), horaFinal.getMinutes(), 0);

      return inicio >= fim ? {horaFinalMenorQueInicial: true} : null;
    };
  };

}
